import { datadogRum } from '@datadog/browser-rum';
import { useRouter } from 'next/router';
import type { FC } from 'react';
import { createContext, useContext, useState, useEffect } from 'react';
import { AuthEnv } from '@peloton/auth/authClient';
import {
  GOOGLE_LOGIN_ERROR_CODE,
  APPLE_LOGIN_ERROR_CODE,
  USER_EXISTS_LOGIN_ERROR_CODE,
  USER_DOES_NOT_EXIST_LOGIN_ERROR_CODE,
  REMOTE_ACTIVATION_ERROR_CODE,
} from '@peloton/auth/OauthProvider';
// eslint-disable-next-line import/no-cycle
import { useCopy } from '../../copy/renderers';
import { toEnvVariables } from '../../env/env';
import { getPersistedAuthData } from '../auth/persistAuthData';
import Toast from '../Toast';

const CONTEXT = createContext({
  shouldShowOopsPage: false,
  setShouldShowOopsPage: (b: boolean) => null as unknown,
  errorMessage: '',
  setErrorMessage: (errorMessage: string) => null as unknown,
  errorCode: '',
  setErrorCode: (errorCode: string) => null as unknown,
});

export const useError = () => useContext(CONTEXT);

export const ErrorProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const googleErrorMessage = useCopy('auth.loggedInWithOtherMethodGoogle') as string;
  const appleErrorMessage = useCopy('auth.loggedInWithOtherMethodApple') as string;
  const userDoesNotExistError = useCopy('auth.userDoesNotExistError') as string;
  const userExistsError = useCopy('auth.userExistsError') as string;
  const defaultError = useCopy('auth.defaultError') as string;
  const remoteActivationError = useCopy('auth.remoteActivationError') as string;

  const loginErrorMessageMap: Record<string, string> = {
    [GOOGLE_LOGIN_ERROR_CODE]: googleErrorMessage,
    [APPLE_LOGIN_ERROR_CODE]: appleErrorMessage,
    [USER_DOES_NOT_EXIST_LOGIN_ERROR_CODE]: userDoesNotExistError,
    [USER_EXISTS_LOGIN_ERROR_CODE]: userExistsError,
    [REMOTE_ACTIVATION_ERROR_CODE]: remoteActivationError,
  };

  const router = useRouter();
  const [shouldShowOopsPage, setShouldShowOopsPage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const isProd = toEnvVariables().ENVIRONMENT === 'PRODUCTION';
  const authEnv = isProd ? AuthEnv.Prod : AuthEnv.Stage;
  const { action } = getPersistedAuthData(authEnv);
  const [errorCode, setErrorCode] = useState('');

  const isActivation = action === 'device_activation';
  const isSocialError =
    errorCode === GOOGLE_LOGIN_ERROR_CODE || errorCode === APPLE_LOGIN_ERROR_CODE;

  useEffect(() => {
    if (!!window) {
      const queryParams = new URLSearchParams(window.location.search);
      const errorMessageFromParams = queryParams.get('errorMessage');

      if (!!errorMessageFromParams) {
        const errorMessageObj = new Error(errorMessageFromParams);
        errorMessageObj.name = isActivation
          ? 'RemoteActivationLoginError'
          : 'ErrorToastShown';
        datadogRum.addError(errorMessageObj, {
          source: isActivation ? 'remote-activation' : 'auth',
          step: 'login-error-redirect',
          errorCode: errorMessageFromParams,
          action: action || 'unknown',
        });
        setErrorCode(errorMessageFromParams);
        setErrorMessage(loginErrorMessageMap[errorMessageFromParams] ?? defaultError);

        router.replace(window.location.pathname, undefined, { shallow: true });
      }
    }
  }, []);

  return (
    <CONTEXT.Provider
      value={{
        shouldShowOopsPage,
        setShouldShowOopsPage,
        errorMessage,
        setErrorMessage,
        errorCode,
        setErrorCode,
      }}
    >
      <div className="toast-container">
        <Toast
          hide={!errorMessage || (isActivation && isSocialError)}
          onClose={() => setErrorMessage('')}
        >
          {errorMessage}
        </Toast>
      </div>
      {children}
      <style jsx>{`
        .toast-container {
          width: 100%;
          display: flex;
          justify-content: center;
        }
      `}</style>
    </CONTEXT.Provider>
  );
};
